import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getActivityLogs } from "../../store/slices/activityLogSlice";
import { useNavigate } from 'react-router-dom';
import {
  Users,
  TestTube,
  TrendingUp,
  Clock,
  Activity,
  FileText,
  Settings,
  ArrowUpRight,
  ArrowDownRight,
  Zap,
  Eye,
  AlertTriangle,
  Download,
  
} from "lucide-react";
import dashboardService from "../../services/dashboardService";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
  BarChart,
  Bar,
} from "recharts";

const Dashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { logs, loading: logsLoading, error: logsError } = useSelector((state) => state.activityLogs);

  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const csvEscape = (value) => {
    if (value === null || value === undefined) return "";
    const str = String(value);
    if (str.includes("\"")) return `"${str.replace(/"/g, '""')}"`;
    if (str.includes(",") || str.includes("\n")) return `"${str}"`;
    return str;
  };

  const downloadCsv = (rows, filename) => {
    const csvContent = rows
      .map((row) => row.map(csvEscape).join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    dispatch(getActivityLogs());
    loadDashboardData();
  }, [dispatch]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const stats = await dashboardService.getDashboardStats();
      setDashboardStats(stats);
      setError(null);
    } catch (err) {
      console.error("Error loading dashboard:", err);
      setError(err.message || "Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  // Chart colors
  const COLORS = {
    primary: ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b'],
    status: {
      completed: '#10b981',
      pending: '#f59e0b',
      in_progress: '#3b82f6',
      cancelled: '#ef4444',
    },
  };

  // Enhanced system stats with real data
  const systemStats = dashboardStats ? [
    {
      label: "Total Users",
      value: dashboardStats.totalUsers.toString(),
      color: "from-blue-500 to-blue-600",
      icon: Users,
      description: "Registered system users",
      trend: "+5",
      trendUp: true,
      trendLabel: "this month",
    },
    {
      label: "Total Tests",
      value: dashboardStats.totalTests.toString(),
      color: "from-purple-500 to-purple-600",
      icon: TestTube,
      description: "Tests in system",
      trend: "+12%",
      trendUp: true,
      trendLabel: "growth",
    },
    {
      label: "Today's Orders",
      value: dashboardStats.todayOrders.toString(),
      color: "from-emerald-500 to-emerald-600",
      icon: TrendingUp,
      description: "Orders processed today",
      trend: "On track",
      trendUp: true,
      trendLabel: "",
    },
    {
      label: "Pending Results",
      value: dashboardStats.pendingOrders.toString(),
      color: "from-orange-500 to-orange-600",
      icon: Clock,
      description: "Awaiting results",
      trend: dashboardStats.urgentOrders.toString(),
      trendUp: false,
      trendLabel: "urgent",
    },
  ] : [];

  // (Optional) Additional status breakdowns can be derived from dashboardStats

  // Prepare pie/user role data (reserved for future charts)

  // Status bars for progress visualization (uses real data)
  const statusBars = dashboardStats ? (() => {
    const completed = Number(dashboardStats.completedOrders || 0);
    const pending = Number(dashboardStats.pendingOrders || 0);
    const inProgress = Number((dashboardStats.ordersByStatus || {}).in_progress || 0);
    const total = completed + pending + inProgress || 1;
    return [
      { status: "Completed", count: completed, color: COLORS.status.completed, percent: Math.round((completed / total) * 100) },
      { status: "In Progress", count: inProgress, color: COLORS.status.in_progress, percent: Math.round((inProgress / total) * 100) },
      { status: "Pending", count: pending, color: COLORS.status.pending, percent: Math.round((pending / total) * 100) },
    ];
  })() : [];

  // Chart datasets from live stats
  const ordersTrendData = dashboardStats?.dailyOrders || [];
  const ordersByStatusData = dashboardStats
    ? Object.entries(dashboardStats.ordersByStatus || {}).map(([key, val]) => ({
        name: key.replace(/_/g, " "),
        key,
        value: Number(val || 0),
        color: COLORS.status[key] || "#94a3b8",
      }))
    : [];
  const usersByRoleData = dashboardStats
    ? Object.entries(dashboardStats.usersByRole || {}).map(([role, count]) => ({
        name: role,
        value: Number(count || 0),
      }))
    : [];

  const handleExportDashboard = () => {
    if (!dashboardStats) return;
    const rows = [];
    rows.push(["Dashboard Summary"]);
    rows.push(["Metric", "Value"]);
    rows.push(["Total Users", dashboardStats.totalUsers]);
    rows.push(["Total Tests", dashboardStats.totalTests]);
    rows.push(["Today's Orders", dashboardStats.todayOrders]);
    rows.push(["Pending Orders", dashboardStats.pendingOrders]);
    rows.push(["Completed Orders", dashboardStats.completedOrders]);
    rows.push(["Urgent Orders", dashboardStats.urgentOrders]);
    rows.push([]);

    rows.push(["Orders By Status"]);
    rows.push(["Status", "Count"]);
    ordersByStatusData.forEach((s) => rows.push([s.name, s.value]));
    rows.push([]);

    rows.push(["Daily Orders"]);
    rows.push(["Date", "Total", "Completed", "Pending", "In Progress", "Urgent"]);
    (dashboardStats.dailyOrders || []).forEach((d) => {
      rows.push([
        d.date || d.day || "",
        d.total ?? "",
        d.completed ?? "",
        d.pending ?? "",
        d.in_progress ?? "",
        d.urgent ?? "",
      ]);
    });
    rows.push([]);

    rows.push(["Users By Role"]);
    rows.push(["Role", "Count"]);
    usersByRoleData.forEach((u) => rows.push([u.name, u.value]));

    downloadCsv(rows, "dashboard-report.csv");
  };

  // Quick actions for the dashboard
  const quickActions = [
    { 
      icon: TestTube, 
      label: "New Test", 
      action: () => navigate('/admin/tests'),
      color: "from-blue-500 to-blue-600",
      description: "Manage tests"
    },
    {
      icon: Users,
      label: "Add Users",
      action: () => navigate("/admin/users"),
      color: "from-purple-500 to-purple-600",
      description: "User management"
    },
    {
      icon: FileText,
      label: "View Reports",
      action: () => navigate("/admin/activity-logs"),
      color: "from-emerald-500 to-emerald-600",
      description: "Activity logs"
    },
    { 
      icon: Settings, 
      label: "Clinic Settings", 
      action: () => navigate("/admin/clinic-settings"),
      color: "from-orange-500 to-orange-600",
      description: "Configure clinic"
    },
  ];

  // Helpers similar to ActivityLogs
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getActionInfo = (action) => {
    const actionMap = {
      login: {
        icon: "🔐",
        color: "bg-blue-100 text-blue-800 border-blue-200",
        label: "User Login",
      },
      register: {
        icon: "👤",
        color: "bg-green-100 text-green-800 border-green-200",
        label: "User Registration",
      },
      patient_registered: {
        icon: "📝",
        color: "bg-purple-100 text-purple-800 border-purple-200",
        label: "Patient Registered",
      },
      test_ordered: {
        icon: "🧪",
        color: "bg-orange-100 text-orange-800 border-orange-200",
        label: "Test Ordered",
      },
      result_entered: {
        icon: "📊",
        color: "bg-teal-100 text-teal-800 border-teal-200",
        label: "Result Entered",
      },
      default: {
        icon: "📋",
        color: "bg-gray-100 text-gray-800 border-gray-200",
        label: action,
      },
    };
    return actionMap[action] || actionMap.default;
  };

  const recentLogs = (logs || [])
    .slice()
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 8);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="relative w-20 h-20 mx-auto">
            <div className="absolute inset-0 border-4 border-gray-200 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-[#235F72] rounded-full border-t-transparent animate-spin"></div>
          </div>
          <p className="text-gray-600 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center space-y-4 max-w-md mx-auto p-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-50 rounded-full">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-xl font-bold text-gray-900">Failed to Load Dashboard</h3>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={loadDashboardData}
            className="px-6 py-3 bg-[#235F72] text-white rounded-xl hover:bg-[#1a4a5a] transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Main Content */}
      <div className="flex-1">
        {/* Dashboard Content */}
        <main className="p-6 space-y-6">
          {/* Header Section */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-[#235F72] to-[#1a4a5a] bg-clip-text text-transparent">
                Dashboard Overview
              </h1>
              <p className="text-gray-600 mt-1">Welcome back! Here's what's happening today.</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleExportDashboard}
                disabled={!dashboardStats}
                className="flex items-center space-x-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Download className="w-4 h-4 text-[#235F72]" />
                <span className="text-sm font-medium text-[#235F72]">Export</span>
              </button>
              <button
                onClick={() => dispatch(getActivityLogs())}
                className="flex items-center space-x-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <Activity className="w-4 h-4 text-[#235F72]" />
                <span className="text-sm font-medium text-[#235F72]">Refresh</span>
              </button>
            </div>
          </div>

          {/* Enhanced System Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {systemStats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  className="relative group bg-white rounded-2xl p-6 border border-gray-200 hover:border-transparent hover:shadow-xl transition-all duration-300 overflow-hidden"
                >
                  {/* Gradient background on hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>
                  
                  {/* Content */}
                  <div className="relative z-10">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.color} shadow-lg`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      {stat.trendUp !== undefined && (
                        <div className={`flex items-center space-x-1 px-2.5 py-1 rounded-lg text-xs font-semibold ${
                          stat.trendUp ? "bg-emerald-50 text-emerald-700" : "bg-orange-50 text-orange-700"
                        }`}>
                          {stat.trendUp ? (
                            <ArrowUpRight className="w-3.5 h-3.5" />
                          ) : (
                            <ArrowDownRight className="w-3.5 h-3.5" />
                          )}
                          <span>{stat.trend}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="space-y-1">
                      <h3 className="text-3xl font-bold text-gray-900">{stat.value}</h3>
                      <p className="text-sm font-semibold text-gray-700">{stat.label}</p>
                      <p className="text-xs text-gray-500">{stat.description}</p>
                      {stat.trendLabel && (
                        <p className="text-xs text-gray-400 mt-1">{stat.trendLabel}</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Quick Actions and Test Status */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-2 mb-6">
                <Zap className="w-5 h-5 text-[#235F72]" />
                <h3 className="text-lg font-bold text-gray-900">Quick Actions</h3>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {quickActions.map((action, index) => {
                  const Icon = action.icon;
                  return (
                    <button
                      key={index}
                      onClick={action.action}
                      className="group relative p-4 border-2 border-gray-200 rounded-xl hover:border-transparent transition-all duration-300 overflow-hidden"
                    >
                      <div className={`absolute inset-0 bg-gradient-to-br ${action.color} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
                      <div className="relative z-10 flex flex-col items-center space-y-2">
                        <div className="p-2 rounded-lg bg-gray-100 group-hover:bg-white/20 transition-colors duration-300">
                          <Icon className="w-5 h-5 text-gray-700 group-hover:text-white transition-colors duration-300" />
                        </div>
                        <span className="text-sm font-semibold text-gray-700 group-hover:text-white transition-colors duration-300 text-center">
                          {action.label}
                        </span>
                        <span className="text-xs text-gray-500 group-hover:text-white/80 transition-colors duration-300">
                          {action.description}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Test Status Overview */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 lg:col-span-2">
              <div className="flex items-center space-x-2 mb-6">
                <TrendingUp className="w-5 h-5 text-[#235F72]" />
                <h3 className="text-lg font-bold text-gray-900">Test Status Overview</h3>
              </div>
              <div className="space-y-5">
                {statusBars.map((item, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center space-x-3">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                        <span className="font-semibold text-gray-900">{item.status}</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className="text-sm text-gray-600">{item.count} tests</span>
                        <span className="text-sm font-bold text-gray-900">{item.percent}%</span>
                      </div>
                    </div>
                    <div className="relative w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700 ease-out relative"
                        style={{ width: `${item.percent}%`, backgroundColor: item.color }}
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <TestTube className="w-4 h-4 text-blue-600" />
                    </div>
                    <span className="text-sm text-gray-600">Total Orders Today: <span className="font-bold text-gray-900">{dashboardStats?.todayOrders || 0}</span></span>
                  </div>
                  <div className="px-3 py-1.5 bg-emerald-50 rounded-lg">
                    <span className="text-sm font-bold text-emerald-700">{dashboardStats?.completionRate || 0}% Completion</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Analytics Charts */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Orders Trend (last 7 days) */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-2 mb-4">
                <TrendingUp className="w-5 h-5 text-[#235F72]" />
                <h3 className="text-lg font-bold text-gray-900">Orders Trend</h3>
              </div>
              {ordersTrendData.length > 0 ? (
                <div className="h-60">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={ordersTrendData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="date" stroke="#94a3b8" tick={{ fontSize: 12 }} />
                      <YAxis stroke="#94a3b8" tick={{ fontSize: 12 }} allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="orders" name="Orders" stroke={COLORS.primary[0]} strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="completed" name="Completed" stroke={COLORS.status.completed} strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="pending" name="Pending" stroke={COLORS.status.pending} strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-sm text-gray-500">No trend data available.</div>
              )}
            </div>

            {/* Orders by Status */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-2 mb-4">
                <TestTube className="w-5 h-5 text-[#235F72]" />
                <h3 className="text-lg font-bold text-gray-900">Orders by Status</h3>
              </div>
              {ordersByStatusData.length > 0 ? (
                <div className="h-60">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={ordersByStatusData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={3}>
                        {ordersByStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-sm text-gray-500">No status data available.</div>
              )}
            </div>

            {/* Users by Role */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-2 mb-4">
                <Users className="w-5 h-5 text-[#235F72]" />
                <h3 className="text-lg font-bold text-gray-900">Users by Role</h3>
              </div>
              {usersByRoleData.length > 0 ? (
                <div className="h-60">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={usersByRoleData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="name" stroke="#94a3b8" tick={{ fontSize: 12 }} />
                      <YAxis stroke="#94a3b8" tick={{ fontSize: 12 }} allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="value" name="Users" fill="#3b82f6" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-sm text-gray-500">No user role data available.</div>
              )}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-2">
                <Activity className="w-5 h-5 text-[#235F72]" />
                <h2 className="text-lg font-bold text-gray-900">Recent Activity</h2>
              </div>
              <button
                onClick={() => navigate("/admin/activity-logs")}
                className="flex items-center space-x-1 text-sm font-medium text-[#235F72] hover:text-[#1a4a5a] transition-colors"
              >
                <span>View All</span>
                <Eye className="w-4 h-4" />
              </button>
            </div>

            {logsLoading ? (
              <div className="py-12 flex flex-col items-center justify-center space-y-4">
                <div className="relative w-16 h-16">
                  <div className="absolute inset-0 border-4 border-gray-200 rounded-full"></div>
                  <div className="absolute inset-0 border-4 border-[#235F72] rounded-full border-t-transparent animate-spin"></div>
                </div>
                <p className="text-sm text-gray-600">Loading recent activity...</p>
              </div>
            ) : logsError ? (
              <div className="py-12 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-50 rounded-full mb-4">
                  <Activity className="w-8 h-8 text-red-500" />
                </div>
                <p className="text-red-600">{String(logsError) || "Failed to load activity"}</p>
              </div>
            ) : (
              <div className="overflow-hidden border border-gray-200 rounded-xl">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                        <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Date & Time</th>
                        <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">User</th>
                        <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Action</th>
                        <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Description</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {recentLogs.length > 0 ? (
                        recentLogs.map((log) => {
                          const actionInfo = getActionInfo(log.action);
                          return (
                            <tr
                              key={log.id}
                              className="hover:bg-gray-50 transition-colors duration-150"
                            >
                              <td className="py-4 px-6 text-sm text-gray-700 font-medium">
                                {formatDate(log.created_at)}
                              </td>
                              <td className="py-4 px-6">
                                <div className="font-semibold text-gray-900">{log.user_name}</div>
                                <div className="text-xs text-gray-500 mt-0.5">{log.user_role}</div>
                              </td>
                              <td className="py-4 px-6">
                                <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold ${actionInfo.color} border`}>
                                  <span className="mr-1.5">{actionInfo.icon}</span>
                                  {actionInfo.label}
                                </span>
                              </td>
                              <td className="py-4 px-6 text-sm text-gray-700">{log.description}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="4" className="py-12 text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                              <FileText className="w-8 h-8 text-gray-400" />
                            </div>
                            <p className="text-gray-500">No recent activities found.</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {recentLogs.length > 0 && (
                  <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between items-center">
                    <span className="text-sm text-gray-600">
                      Showing latest {recentLogs.length} activities
                    </span>
                    <button
                      onClick={() => navigate("/admin/activity-logs")}
                      className="text-sm font-medium text-[#235F72] hover:text-[#1a4a5a] transition-colors"
                    >
                      View All Activity →
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Add shimmer animation to styles */}
      <style jsx>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
